import * as ed25519 from '@noble/ed25519'
import { sha512 } from '@noble/hashes/sha2'
import { PublicKey, Transaction, TransactionInstruction } from '@solana/web3.js'
import bs58 from 'bs58'

import { assertValidSignatureInput } from '../common/validation'

import type {
  SolanaSigningPayload,
  SolanaTransaction,
  SignatureInput,
  ValidationResult,
} from '../../types'

ed25519.etc.sha512Sync = (...msgs: Uint8Array[]) =>
  sha512(ed25519.etc.concatBytes(...msgs))

/**
 * Validates a Solana transaction against an expected payload.
 */
export function validateSolanaSignature(
  payload: SolanaSigningPayload,
  signature: SignatureInput,
): ValidationResult {
  try {
    assertValidSignatureInput(signature.signature)
    if (!/^[1-9A-HJ-NP-Za-km-z]{87,88}$/.test(signature.signature)) {
      return {
        success: false,
        message: 'Invalid Solana signature format',
        details:
          'Signature must be Base58 encoded, 87-88 characters (64 bytes when decoded)',
      }
    }

    const signatureBytes = base58Decode(signature.signature)
    if (!signatureBytes || signatureBytes.length !== 64) {
      return {
        success: false,
        message: 'Invalid signature length after decoding',
        details: 'Ed25519 signatures must be exactly 64 bytes when decoded',
      }
    }

    // Validate public key and decode to bytes
    const publicKeyBytes = base58Decode(payload.walletAddress)
    if (!publicKeyBytes || publicKeyBytes.length !== 32) {
      return {
        success: false,
        message: 'Invalid public key format',
        details: 'Solana public keys must be 32 bytes when decoded',
      }
    }
    // Additional structural validation via web3.js
    new PublicKey(payload.walletAddress)

    const transaction = reconstructTransaction(payload.payload)
    const messageBytes = transaction.serializeMessage()

    const isValid = ed25519.verify(signatureBytes, messageBytes, publicKeyBytes)
    if (!isValid) {
      return {
        success: false,
        message: 'Ed25519 signature verification failed',
        details: 'The signature does not match the transaction and public key',
      }
    }

    return {
      success: true,
      message: 'Solana signature verified successfully',
      details: `Ed25519 signature verified for public key ${payload.walletAddress}`,
    }
  } catch (error) {
    return {
      success: false,
      message: 'Signature verification failed',
      details:
        error instanceof Error ? error.message : 'Unknown cryptographic error',
    }
  }
}

function base58Decode(str: string): Uint8Array | null {
  try {
    return new Uint8Array(bs58.decode(str))
  } catch {
    return null
  }
}

function reconstructTransaction(txData: SolanaTransaction): Transaction {
  const tx = new Transaction()
  tx.recentBlockhash = txData.recentBlockhash
  tx.feePayer = new PublicKey(txData.feePayer)
  for (const ix of txData.instructions) {
    tx.add(
      new TransactionInstruction({
        programId: new PublicKey(ix.programId),
        keys: ix.keys.map((k) => ({
          pubkey: new PublicKey(k.pubkey),
          isSigner: k.isSigner,
          isWritable: k.isWritable,
        })),
        // Handle both string and number array data formats - browser compatible
        data: (typeof ix.data === 'string'
          ? new TextEncoder().encode(ix.data)
          : new Uint8Array(ix.data)) as Buffer,
      }),
    )
  }
  return tx
}
